import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  ApprovePayrollMutation,
  ApprovePayrollMutationVariables,
  ApproveSessionTokenPayrollMutation,
  ApproveSessionTokenPayrollMutationVariables,
  CancelPayrollMutation,
  CancelPayrollMutationVariables,
  CancelPayrollsByBankIdMutation,
  CancelPayrollsByBankIdMutationVariables,
  CreateTopOffContributionsMutation,
  CreateTopOffContributionsMutationVariables,
  DismissPayrollMutation,
  DismissPayrollMutationVariables,
  GetPaginatedChangeRequestsQuery,
  GetPaginatedChangeRequestsQueryVariables,
  GetPaginatedContributionsQuery,
  GetPaginatedContributionsQueryVariables,
  GetPayrollHistoryQuery,
  GetPayrollLineItemsQuery,
  GetPayrollLineItemsQueryVariables,
  GetTotalAmountSavedQuery,
  RecheckPayrollBalanceMutation,
  RecheckPayrollBalanceMutationVariables,
} from "types/graphql";

// get users financial accounts
export function getPayrollLineItems(
  client: ApolloClient<object>,
  groupId: string,
  payPeriodId?: string | null
): Promise<GetPayrollLineItemsQuery["getPayrollLineItems"]> {
  return new Promise((resolve, reject) => {
    const GET_LINE_ITEMS = gql`
      query getPayrollLineItems($groupId: ID!, $payPeriodId: ID) {
        getPayrollLineItems(groupId: $groupId, payPeriodId: $payPeriodId) {
          pendingContributionChanges {
            employeeId
            id
            userCurrentContributionAmount
            userNewContributionAmount
            isTopOffContribution
          }
          usersWhoLeftPlan {
            id
            employeeId
            userProfile {
              firstName
              lastName
              email
              lastFourOfSsn
              dob
            }
            contribution {
              id
              employeeId
              amount
            }
          }

          contributionsWithNoChange {
            id
            employeeId
            amount
          }
        }
      }
    `;
    const result = client.query<
      GetPayrollLineItemsQuery,
      GetPayrollLineItemsQueryVariables
    >({
      query: GET_LINE_ITEMS,
      variables: {
        groupId,
        payPeriodId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data.getPayrollLineItems);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getPaginatedChangeRequests(
  client: ApolloClient<object>,
  {
    limit,
    offset,
    groupId,
    excludedIds,
    payPeriodId,
  }: GetPaginatedChangeRequestsQueryVariables
): Promise<GetPaginatedChangeRequestsQuery["getPaginatedChangeRequests"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getPaginatedChangeRequests(
        $limit: Int!
        $offset: Int!
        $groupId: ID!
        $excludedIds: [ID]
        $payPeriodId: ID!
      ) {
        getPaginatedChangeRequests(
          limit: $limit
          offset: $offset
          groupId: $groupId
          excludedIds: $excludedIds
          payPeriodId: $payPeriodId
        ) {
          count
          rows {
            employeeId
            id
            userCurrentContributionAmount
            userNewContributionAmount
            isTopOffContribution
            userProfile {
              firstName
              lastName
              email
              lastFourOfSsn
              dob
            }
          }
        }
      }
    `;
    const result = client.query<
      GetPaginatedChangeRequestsQuery,
      GetPaginatedChangeRequestsQueryVariables
    >({
      query: REQUEST,
      variables: { limit, offset, groupId, excludedIds, payPeriodId },
    });
    result.then(
      (data) => {
        resolve(data.data.getPaginatedChangeRequests);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getPaginatedContributions(
  client: ApolloClient<object>,
  {
    limit,
    offset,
    groupId,
    excludedIds,
    payPeriodId,
  }: GetPaginatedContributionsQueryVariables
): Promise<GetPaginatedContributionsQuery["getPaginatedContributions"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getPaginatedContributions(
        $limit: Int!
        $offset: Int!
        $groupId: ID!
        $excludedIds: [ID]
        $payPeriodId: ID!
      ) {
        getPaginatedContributions(
          limit: $limit
          offset: $offset
          groupId: $groupId
          excludedIds: $excludedIds
          payPeriodId: $payPeriodId
        ) {
          count
          rows {
            ... on Contribution {
              employeeId
              id
              amount
              userProfile {
                firstName
                lastName
                email
                lastFourOfSsn
                dob
              }
            }

            ... on ContributionChangeRequest {
              employeeId
              id
              userCurrentContributionAmount
              userNewContributionAmount
              isTopOffContribution
              userProfile {
                firstName
                lastName
                email
                lastFourOfSsn
                dob
              }
            }
          }
        }
      }
    `;
    const result = client.query<
      GetPaginatedContributionsQuery,
      GetPaginatedContributionsQueryVariables
    >({
      query: REQUEST,
      variables: { limit, offset, groupId, excludedIds, payPeriodId },
    });
    result.then(
      (data) => {
        resolve(data.data.getPaginatedContributions);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function approvePayroll(
  client: ApolloClient<object>,
  payrollInput: ApprovePayrollMutationVariables["payrollInput"]
): Promise<ApprovePayrollMutation["approvePayroll"] | null> {
  return new Promise((resolve, reject) => {
    const PROCESS_PAYROLL = gql`
      mutation approvePayroll($payrollInput: ScheduledPayrollInput!) {
        approvePayroll(input: $payrollInput) {
          start
          end
          payrollProcessingDate
          onOrAfterPayrollProcessingDate
        }
      }
    `;

    const result = client.mutate<
      ApprovePayrollMutation,
      ApprovePayrollMutationVariables
    >({
      mutation: PROCESS_PAYROLL,
      variables: {
        payrollInput,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.approvePayroll ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getPayrollHistoryRequest(
  client: ApolloClient<object>
): Promise<NonNullable<GetPayrollHistoryQuery["company"]>> {
  return new Promise((resolve, reject) => {
    const request = gql`
      query getPayrollHistory {
        company {
          payrollHistory {
            id
            period {
              start
              end
              displayStart
              displayEnd
            }
            totalAmount
            status
            bankId
            isBankLinked
            payrollDocumentId
            groupName
          }
        }
      }
    `;
    const result = client.query<GetPayrollHistoryQuery>({
      query: request,
    });
    result.then(
      (data) => {
        if (data.data.company) {
          resolve(data.data.company);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo result
          reject(data);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function cancelPayrollsByBankIdRequest(
  client: ApolloClient<object>,
  bankId: string
): Promise<
  NonNullable<CancelPayrollsByBankIdMutation["cancelPayrollsByBankId"]>
> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation cancelPayrollsByBankId($bankId: ID!) {
        cancelPayrollsByBankId(bankId: $bankId)
      }
    `;
    const result = client.mutate<
      CancelPayrollsByBankIdMutation,
      CancelPayrollsByBankIdMutationVariables
    >({
      mutation: request,
      variables: {
        bankId,
      },
    });
    result.then(
      (data) => {
        const cancelled = data.data?.cancelPayrollsByBankId;
        if (cancelled) {
          resolve(cancelled);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo result
          reject(data);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getTotalAmountSavedRequest(
  client: ApolloClient<object>
): Promise<NonNullable<GetTotalAmountSavedQuery["getTotalAmountSaved"]>> {
  return new Promise((resolve, reject) => {
    const request = gql`
      query getTotalAmountSaved {
        getTotalAmountSaved {
          totalAmount
        }
      }
    `;
    const result = client.query<GetTotalAmountSavedQuery>({
      query: request,
    });
    result.then(
      (data) => {
        if (data.data.getTotalAmountSaved) {
          resolve(data.data.getTotalAmountSaved);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo result
          reject(data);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function createTopOffContributionsRequest(
  client: ApolloClient<object>,
  groupId: string,
  payPeriodId: string
): Promise<
  NonNullable<CreateTopOffContributionsMutation["createTopOffContributions"]>
> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation createTopOffContributions($groupId: ID!, $payPeriodId: ID!) {
        createTopOffContributions(groupId: $groupId, payPeriodId: $payPeriodId)
      }
    `;
    const result = client.mutate<
      CreateTopOffContributionsMutation,
      CreateTopOffContributionsMutationVariables
    >({
      mutation: request,
      variables: {
        groupId,
        payPeriodId,
      },
    });
    result.then(
      (data) => {
        const created = data.data?.createTopOffContributions;
        if (created) {
          resolve(created);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo result
          reject(data);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function cancelPayrollRequest(
  client: ApolloClient<object>,
  groupId: string,
  payrollId: string
): Promise<NonNullable<CancelPayrollMutation["cancelPayroll"]>> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation cancelPayroll($groupId: ID!, $payrollId: ID!) {
        cancelPayroll(groupId: $groupId, payrollId: $payrollId) {
          start
          end
          payrollProcessingDate
          onOrAfterPayrollProcessingDate
          status
          gracePeriodEndDate
          payPeriodStart
          payPeriodEnd
          payrollId
        }
      }
    `;
    const result = client.mutate<
      CancelPayrollMutation,
      CancelPayrollMutationVariables
    >({
      mutation: request,
      variables: {
        groupId,
        payrollId,
      },
    });
    result.then(
      (data) => {
        const cancelled = data.data?.cancelPayroll;
        if (cancelled) {
          resolve(cancelled);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo result
          reject(data);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function recheckPayrollBalanceRequest(
  client: ApolloClient<object>,
  payrollId: string
): Promise<
  NonNullable<RecheckPayrollBalanceMutation["recheckPayrollBalance"]>
> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation recheckPayrollBalance($payrollId: ID!) {
        recheckPayrollBalance(payrollId: $payrollId)
      }
    `;
    const result = client.mutate<
      RecheckPayrollBalanceMutation,
      RecheckPayrollBalanceMutationVariables
    >({
      mutation: request,
      variables: {
        payrollId,
      },
    });
    result.then(
      (data) => {
        const rechecked = data.data?.recheckPayrollBalance;
        if (rechecked) {
          resolve(rechecked);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo result
          reject(data);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function dismissPayroll(
  client: ApolloClient<object>,
  payPeriodId: string,
  reason: string
): Promise<DismissPayrollMutation["dismissPayroll"] | null> {
  return new Promise((resolve, reject) => {
    const DISMISS_PAYROLL = gql`
      mutation dismissPayroll($payPeriodId: ID!, $reason: String!) {
        dismissPayroll(payPeriodId: $payPeriodId, reason: $reason)
      }
    `;

    const result = client.mutate<
      DismissPayrollMutation,
      DismissPayrollMutationVariables
    >({
      mutation: DISMISS_PAYROLL,
      variables: {
        payPeriodId,
        reason,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.dismissPayroll ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function approveSessionTokenPayroll(
  client: ApolloClient<object>,
  { sessionToken, identityHash }: ApproveSessionTokenPayrollMutationVariables
): Promise<
  ApproveSessionTokenPayrollMutation["approveSessionTokenPayroll"] | null
> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation approveSessionTokenPayroll(
        $sessionToken: ID!
        $identityHash: String!
      ) {
        approveSessionTokenPayroll(
          sessionToken: $sessionToken
          identityHash: $identityHash
        )
      }
    `;

    const result = client.mutate<
      ApproveSessionTokenPayrollMutation,
      ApproveSessionTokenPayrollMutationVariables
    >({
      mutation: MUTATION,
      variables: {
        sessionToken,
        identityHash,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.approveSessionTokenPayroll ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export default {
  getPayrollLineItems,
  approvePayroll,
  getPayrollHistoryRequest,
  cancelPayrollsByBankIdRequest,
  getTotalAmountSavedRequest,
  createTopOffContributionsRequest,
  getPaginatedChangeRequests,
  getPaginatedContributions,
  cancelPayrollRequest,
  recheckPayrollBalanceRequest,
  dismissPayroll,
  approveSessionTokenPayroll,
};
